import React, { useState, useRef } from 'react';
import SEO from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, Printer, Map as MapIcon, X, Image as ImageIcon } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { trackToolUsage } from '@/lib/tracking';
import AppLogo from '@/assets/logo.svg?react';
import DemoMap from '@/assets/sitemap-demo.svg';

interface Marker {
  id: string;
  type: 'silt-fence' | 'inlet' | 'exit' | 'washout';
  x: number;
  y: number;
}

const MapSketcherPage: React.FC = () => {
  const [image, setImage] = useState<string | null>(null);
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [selectedTool, setSelectedTool] = useState<Marker['type'] | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (ev.target?.result) {
            setImage(ev.target.result as string);
            setMarkers([]); 
            trackToolUsage('map_sketcher', 'upload_image');
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const loadDemo = () => {
      setImage(DemoMap);
      setMarkers([
          { id: 'demo1', type: 'exit', x: 42, y: 88 },
          { id: 'demo2', type: 'silt-fence', x: 50, y: 12 },
          { id: 'demo3', type: 'inlet', x: 12.5, y: 18.5 },
      ]);
      trackToolUsage('map_sketcher', 'load_demo');
  };

  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectedTool || !image) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setMarkers([...markers, { id: crypto.randomUUID(), type: selectedTool, x, y }]);
  };

  const removeMarker = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMarkers(markers.filter(m => m.id !== id));
  };

  const handlePrint = () => {
    trackToolUsage('map_sketcher', 'export', { marker_count: markers.length });
    window.print();
  };

  const getIcon = (type: Marker['type']) => {
    // Inline styles ensure colors persist even if browser "Background Graphics" setting is off
    switch (type) {
      case 'silt-fence': return <div className="w-8 h-1 transform -rotate-45" style={{ backgroundColor: 'black', border: '1px solid white' }} title="Silt Fence" />;
      case 'inlet': return <div className="w-4 h-4 rounded-full shadow-sm" style={{ backgroundColor: '#ef4444', border: '2px solid white' }} title="Inlet Protection" />;
      case 'exit': return <div className="w-6 h-6 flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: '#78716c', border: '2px solid white', color: 'white' }} title="Construction Exit">TC</div>;
      case 'washout': return <div className="w-5 h-5" style={{ backgroundColor: '#3b82f6', border: '2px solid white' }} title="Concrete Washout" />;
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "SWPPP Map Sketcher",
    "applicationCategory": "DesignApplication",
    "operatingSystem": "Web",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
    "description": "Free online tool to create SWPPP site maps. Upload blueprints and drag-and-drop erosion control symbols like silt fences and inlet protection."
  };

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-indigo-500/20 print:bg-white print:h-screen print:overflow-hidden">
      <SEO 
        title="Free SWPPP Map Maker - Create Site Plans Online"
        description="Upload your site plan and drag-and-drop standard SWPPP symbols. Export to PDF for your Stormwater Pollution Prevention Plan."
        canonical="/tools/map-sketcher"
        jsonLd={jsonLd}
      />
      
      {/* Strict Print CSS for Single Page Fit */}
      <style>{`
        @media print {
            @page { 
                margin: 0.25cm; 
                size: landscape; 
            }
            body { 
                -webkit-print-color-adjust: exact !important; 
                print-color-adjust: exact !important; 
                background: white !important;
            }
            /* Hide everything by default, we will expose specific children */
            .print-hidden { display: none !important; }
            
            /* Container for the single page content */
            .print-page-container {
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                display: flex;
                flex-direction: column;
                background: white;
                z-index: 9999;
                padding: 10px;
            }

            .print-header {
                flex-shrink: 0;
                margin-bottom: 10px;
                border-bottom: 2px solid #e5e7eb;
                padding-bottom: 10px;
            }

            /* The map wrapper takes remaining space */
            .print-map-wrapper {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                position: relative;
            }

            /* The image inside needs to fit */
            .print-map-content {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
                width: auto;
                height: auto;
            }
            
            /* Hide delete buttons on markers */
            .print-marker-delete { display: none !important; }
        }
      `}</style>

      {/* This container becomes the entire page in Print Mode */}
      <div className={image ? "print-page-container" : ""}>
          
          {/* Print-Only Branded Header */}
          <div className="hidden print-header print:flex items-center justify-between">
             <div className="flex items-center gap-2">
                <AppLogo className="h-8 w-8 text-indigo-600" />
                <div>
                    <span className="font-bold text-xl block leading-none">RunOffTrack</span>
                    <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Compliance Tools</span>
                </div>
             </div>
             <div className="text-right">
                <div className="text-sm font-bold">SWPPP Site Map</div>
                <div className="text-xs text-muted-foreground">Generated {new Date().toLocaleDateString()}</div>
             </div>
          </div>

          <div className="bg-muted/30 py-20 border-b border-border print:hidden">
            <div className="container px-4 text-center max-w-4xl mx-auto">
            <Breadcrumbs items={[
                { label: 'Resources', href: '/' },
                { label: 'Map Sketcher', href: '/tools/map-sketcher' }
            ]} />

            <div className="inline-flex items-center justify-center p-3 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full mb-6">
                <MapIcon size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                SWPPP Map Sketcher
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                The easiest way to update a site map without expensive CAD software. Upload your plan, place BMPs, and print to PDF.
            </p>
            </div>
          </div>

          <div className="container px-4 py-8 max-w-6xl mx-auto print:p-0 print:w-full print:h-full print:max-w-none">
            <div className="grid lg:grid-cols-4 gap-8 print:block print:h-full">
                
                {/* Toolbar - Hidden on Print */}
                <Card className="print:hidden h-fit">
                    <CardContent className="p-6 space-y-6">
                        <div className="space-y-3">
                            <label className="text-sm font-semibold">1. Load Site Plan</label>
                            <Button variant="outline" className="w-full" onClick={() => imageInputRef.current?.click()}>
                                <Upload className="mr-2 h-4 w-4" /> Upload Image
                            </Button>
                            <Button variant="ghost" className="w-full text-xs text-muted-foreground" onClick={loadDemo}>
                                <ImageIcon className="mr-2 h-3 w-3" /> Try Demo Map
                            </Button>
                            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                        </div>

                        <div className="space-y-3">
                            <label className="text-sm font-semibold">2. Select Tool</label>
                            <div className="grid grid-cols-2 gap-2">
                                <Button variant={selectedTool === 'silt-fence' ? 'default' : 'secondary'} className="text-xs" onClick={() => setSelectedTool('silt-fence')}>Silt Fence</Button>
                                <Button variant={selectedTool === 'inlet' ? 'default' : 'secondary'} className="text-xs" onClick={() => setSelectedTool('inlet')}>Inlet Prot.</Button>
                                <Button variant={selectedTool === 'exit' ? 'default' : 'secondary'} className="text-xs" onClick={() => setSelectedTool('exit')}>Const. Exit</Button>
                                <Button variant={selectedTool === 'washout' ? 'default' : 'secondary'} className="text-xs" onClick={() => setSelectedTool('washout')}>Washout</Button>
                            </div>
                            <p className="text-[10px] text-muted-foreground">
                                Click on the map to place the selected symbol. Click a symbol to remove it.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-semibold">3. Export</label>
                            <Button onClick={handlePrint} className="w-full" disabled={!image}>
                                <Printer className="mr-2 h-4 w-4" /> Print / Save PDF
                            </Button>
                        </div>

                        <p className="text-xs text-muted-foreground pt-4 border-t">
                            Tip: Use landscape mode when printing to PDF for best results.
                        </p>
                    </CardContent>
                </Card>

                {/* Canvas Area */}
                <div className={`lg:col-span-3 print-map-wrapper ${image ? '' : 'print:hidden'}`}>
                    <div className={`bg-muted/10 border-2 border-dashed border-border rounded-xl min-h-[600px] flex items-center justify-center relative overflow-hidden print:border-none print:min-h-0 print:bg-transparent ${image ? 'print-map-content' : ''}`}>
                        {!image ? (
                            <div className="text-center text-muted-foreground print:hidden">
                                <MapIcon className="mx-auto h-12 w-12 mb-2 opacity-20" />
                                <p>Upload a site plan image to begin</p>
                            </div>
                        ) : (
                            <div 
                                className="relative inline-block" // Changed to inline-block for tight wrapping around image
                                onClick={handleMapClick}
                            >
                                <img src={image} alt="Site Plan" className="max-w-full h-auto block print:max-h-[85vh] print:w-auto" />
                                {markers.map(marker => (
                                    <div 
                                        key={marker.id}
                                        className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
                                        style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
                                    >
                                        {getIcon(marker.type)}
                                        <button 
                                            onClick={(e) => removeMarker(marker.id, e)}
                                            className="absolute -top-4 -right-4 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity print-marker-delete shadow-sm"
                                        >
                                            <X size={10} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* SEO Content Section - Hidden on Print */}
            <section className="mt-24 pt-16 border-t border-border print:hidden">
                <div className="max-w-3xl mx-auto prose dark:prose-invert">
                    <h2>Why use a Digital SWPPP Map?</h2>
                    <p>
                        The SWPPP site map is a living document. As construction phases progress, erosion controls move. 
                        Hand-drawing updates on a muddy paper map is messy and unprofessional.
                    </p>
                    <p>
                        With our <strong>Online Map Sketcher</strong>, you can:
                    </p>
                    <ul>
                        <li>Quickly mark the location of new <strong>Concrete Washouts</strong>.</li>
                        <li>Update perimeter controls like <strong>Silt Fences</strong> as grading limits change.</li>
                        <li>Indicate <strong>Construction Exits</strong> clearly for delivery drivers.</li>
                        <li>Export a clean, legible PDF to keep in your SWPPP binder for inspectors.</li>
                    </ul>

                    <h3>Standard SWPPP Symbols Used</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 not-prose my-6">
                        <div className="flex items-center gap-3 p-3 border rounded bg-card">
                            <div className="w-8 h-1 transform -rotate-45" style={{ backgroundColor: 'black' }} />
                            <span className="text-sm font-medium">Silt Fence</span>
                        </div>
                        <div className="flex items-center gap-3 p-3 border rounded bg-card">
                            <div className="w-4 h-4 rounded-full shadow-sm" style={{ backgroundColor: '#ef4444' }} />
                            <span className="text-sm font-medium">Inlet Protection</span>
                        </div>
                        <div className="flex items-center gap-3 p-3 border rounded bg-card">
                            <div className="w-6 h-6 flex items-center justify-center text-[10px] text-white font-bold" style={{ backgroundColor: '#78716c' }}>TC</div>
                            <span className="text-sm font-medium">Stabilized Exit</span>
                        </div>
                        <div className="flex items-center gap-3 p-3 border rounded bg-card">
                            <div className="w-5 h-5" style={{ backgroundColor: '#3b82f6' }} />
                            <span className="text-sm font-medium">Concrete Washout</span>
                        </div>
                    </div>

                    <h3>Need more features?</h3>
                    <p>
                        The full RunOffTrack platform includes interactive, geo-referenced maps that allow you to pin photos directly to specific locations on your site plan. 
                        <a href="/pricing" className="text-primary font-medium hover:underline ml-1">View Plans &rarr;</a>
                    </p>
                </div>
            </section>
          </div>
      </div>
    </div>
  );
};

export default MapSketcherPage;
